import React, { useEffect, useState } from 'react';
import contact_us_img from '../../assets/images/contact_us_img.jpg'
import cl from './ContactForm.module.css'
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const Notification = ({ type, message, onClose, isVisible }) => {
  if (!isVisible) return null;

  const backgroundColor = type === 'success' ? '#142937' : '#142937';
  const borderColor = type === 'success' ? '#4CAF50' : '#F44336';

  return (
    <div 
      className={cl.notification} 
      style={{ 
        backgroundColor: backgroundColor,
        borderLeft: `4px solid ${borderColor}`
      }}
    >
      <div className={cl.notificationContent}>
        <span className={cl.message}>{message}</span>
        <button className={cl.closeButton} onClick={onClose}>
          ×
        </button>
      </div>
    </div>
  );
};

const ContactForm = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    message: '',
    consent: false
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    companyName: false,
    message: false,
    consent: false
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    companyName: '',
    message: '',
    consent: ''
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const [notification, setNotification] = useState({
    isVisible: false,
    type: '', // 'success' или 'error'
    message: ''
  });

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const showNotification = (type, message) => {
    setNotification({
      isVisible: true,
      type,
      message
    });

    // Автоматически скрываем через 5 секунд
    setTimeout(() => {
      hideNotification();
    }, 5000);
  };

  // Валидация отдельного поля
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !value ? 'Name is required' : '';
      case 'email':
        return !value ? 'Email is required' : '';
      case 'companyName':
        return !value ? 'Company name is required' : '';
      case 'message':
        return !value ? 'Message is required' : '';
      case 'consent':
        return !value ? 'You must consent to proceed' : '';
      default:
        return '';
    }
  };

  // Валидация всей формы
  const validateForm = () => {
    const newErrors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      companyName: validateField('companyName', formData.companyName),
      message: validateField('message', formData.message),
      consent: validateField('consent', formData.consent)
    };

    setErrors(newErrors);

    // Проверяем, все ли поля заполнены и consent = true
    const isValid = Object.values(formData).every(value => {
      if (typeof value === 'boolean') {
        return value === true; // Для checkbox проверяем true
      }
      return value.trim() !== ''; // Для строк проверяем не пустоту
    });

    setIsFormValid(isValid);
    return isValid;
  };

  // Обработчик изменения полей формы
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Если поле уже было тронуто, валидируем его сразу
    if (touched[name]) {
      const error = validateField(name, type === 'checkbox' ? checked : value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  // Обработчик потери фокуса (поле было тронуто)
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Валидируем поле после потери фокуса
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Обработчик отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Помечаем все поля как тронутые при отправке
    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    if (validateForm()) {
      console.log('Form is valid, submitting:', formData);
      
      const dataForEmail = {
        project: 'fortres',
        subject: '/',
        data: formData
      };

      try {
        const response = await axios.post('https://assembly.tehnik.tech/api/send_form/from', dataForEmail, {
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        showNotification('success', 'Message sent successfully! We will contact you soon.');

        // Сбрасываем форму после успешной отправки
        setFormData({
          name: '',
          email: '',
          companyName: '',
          message: '',
          consent: false
        });

        // Сбрасываем состояние touched
        setTouched({
          name: false,
          email: false,
          companyName: false,
          message: false,
          consent: false
        });

      } catch (error) {
        showNotification('error', 'Error sending message. Please try again.');
      }
    } else {
      console.log('Form is not valid');
    }
  };

  // Валидируем форму при изменении данных
  useEffect(() => {
    validateForm();
  }, [formData]);

  return (
    <div className={cl.contact_us_container} id='contact-form'>
      <Notification
        type={notification.type}
        message={notification.message}
        onClose={hideNotification}
        isVisible={notification.isVisible}
      />
      <h2 className={cl.contact_us_title}>
        Contact Us
      </h2>
      <div className={cl.contact_us_form_wrapper}>
        <div className={cl.contact_us_left_side}>
          <img src={contact_us_img} alt="#contact_us_img" />
        </div>
        <div className={cl.contact_us_right_side}>
          <form onSubmit={handleSubmit}>
            <div className={cl.input_wrapper}>
              <input 
                type="text" 
                name="name"
                placeholder='Your name' 
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {touched.name && errors.name && <span className={cl.error}>{errors.name}</span>}
            </div>
            <div className={cl.input_wrapper}>
              <input 
                type="email" 
                name="email"
                placeholder='Email Address' 
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {touched.email && errors.email && <span className={cl.error}>{errors.email}</span>}
            </div>
            <div className={cl.input_wrapper}>
              <input 
                type="text" 
                name="companyName"
                placeholder='Company name' 
                value={formData.companyName}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {touched.companyName && errors.companyName && <span className={cl.error}>{errors.companyName}</span>}
            </div>
            <div className={cl.textarea_wrapper}>
              <textarea 
                name="message"
                placeholder='Message'
                value={formData.message}
                onChange={handleInputChange}
                onBlur={handleBlur}
              ></textarea>
              {touched.message && errors.message && <span className={cl.error}>{errors.message}</span>}
            </div>
            <div className={cl.checker_wrapper}>
              <input 
                type="checkbox" 
                name="consent"
                checked={formData.consent}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              <div>
                <span className={cl.checkbox_text}>
                  I consent to having this website store my submitted information so they can respond to my inquiry.
                </span>
                {touched.consent && errors.consent && <div className={cl.error}>{errors.consent}</div>}
              </div>
            </div>
            <button 
              type="submit" 
              className={`${cl.contact_us_btn} ${!isFormValid ? cl.disabled : ''}`}
              disabled={!isFormValid}
            >
              Contact Now
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;